import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Input, RadioButton } from '@aimsos/ui';
import { Trash2 } from 'lucide-react';

/**
 * One phone number: the primary marker, the number and a way to remove it.
 * Callbacks take the row id so the parent can hand every row the same stable
 * handlers and `memo` holds while a neighbour is edited.
 */
const PhoneRow = memo(function PhoneRow({ phone, index, placeholder, error, onChange, onMakePrimary, onRemove }) {
  const { t } = useTranslation('unified-profiles');

  const handleChange = useCallback((event) => onChange(phone.id, event.target.value), [onChange, phone.id]);
  const handlePrimary = useCallback(() => onMakePrimary(phone.id), [onMakePrimary, phone.id]);
  const handleRemove = useCallback(() => onRemove(phone.id), [onRemove, phone.id]);

  return (
    <div
      role="group"
      aria-label={`${t('create.fields.phones.number')} ${index + 1}`}
      className="flex items-start gap-3"
    >
      <div className="flex h-11 items-center">
        <RadioButton
          aria-label={t('create.fields.phones.makePrimary')}
          checked={phone.isPrimary}
          onChange={handlePrimary}
        />
      </div>
      <div className="min-w-0 flex-1">
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          aria-label={t('create.fields.phones.number')}
          placeholder={placeholder}
          value={phone.number}
          error={error ? t('create.errors.phone') : undefined}
          onChange={handleChange}
        />
      </div>
      <div className="flex h-11 items-center">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={Trash2}
          aria-label={t('create.fields.phones.remove')}
          onClick={handleRemove}
        />
      </div>
    </div>
  );
});

export default PhoneRow;
